import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, CalendarClock, X } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set the studio's real WhatsApp number (country code + digits, e.g. "919876543210")
// to turn the callback card into a live WhatsApp chat button.
const WHATSAPP_NUMBER = "919211877226";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi Griham Design, I'd like to discuss an interior project."
)}`;

const TIME_SLOTS = [
  { label: "Weekday Morning", detail: "10 AM - 12 PM", testId: "booking-slot-weekday-morning" },
  { label: "Weekday Evening", detail: "5 PM - 7 PM", testId: "booking-slot-weekday-evening" },
  { label: "Saturday", detail: "11 AM - 1 PM", testId: "booking-slot-saturday" },
];

const EMPTY_FORM = {
  name: "",
  phone: "",
  project_type: "New Home / Apartment",
  location: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    const handler = (e) =>
      setForm((prev) => ({ ...prev, message: e.detail }));
    window.addEventListener("griham:prefill", handler);
    return () => window.removeEventListener("griham:prefill", handler);
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const pickSlot = (slot) => {
    const note = `Preferred consultation slot: ${slot.label} (${slot.detail}).`;
    setForm((prev) => ({
      ...prev,
      message: prev.message ? `${prev.message}\n${note}` : note,
    }));
    setBookingOpen(false);
    toast.success(`Slot preference added: ${slot.label} ${slot.detail}. Submit the form to confirm.`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! Your enquiry has been received. We will get back within 24 hours.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 bg-[#F4EDE2] border border-[#0D2121]/20 text-sm text-[#0D2121] focus:outline-none focus:border-[#E2A435]";
  const labelClass = "block text-xs uppercase tracking-wider text-[#7A6F66] mb-1 font-semibold";

  return (
    <section id="contact" className="py-24 px-6 md:px-10 bg-[#0D2121] text-[#FAF6F0]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5 space-y-6">
          <span className="text-xs uppercase tracking-widest text-[#E2A435] font-semibold">Start Your Project</span>
          <h2 className="font-display text-3xl sm:text-5xl font-light text-[#FAF6F0] leading-tight">
            Let&rsquo;s shape your space together
          </h2>
          <p className="text-[#EADBC8]/80 text-base leading-relaxed">
            Tell us about your home, renovation, or commercial space. We will review your details and schedule an initial consultation call.
          </p>

          <div className="space-y-4 pt-4 border-t border-[#FAF6F0]/20">
            <div className="flex items-center justify-between gap-4 p-4 bg-[#FAF6F0]/5 border border-[#FAF6F0]/10">
              <div>
                <div className="text-xs text-[#EADBC8]/70">
                  {WHATSAPP_NUMBER ? "Prefer instant messaging?" : "Fastest reply?"}
                </div>
                <div className="text-sm font-medium text-[#FAF6F0]">
                  {WHATSAPP_NUMBER ? "Chat with Griham Design" : "We call back within 24 hours"}
                </div>
              </div>
              {WHATSAPP_NUMBER ? (
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-chat-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-[#E2A435] text-[#0D2121] hover:bg-[#c88c2a] transition-colors whitespace-nowrap"
                >
                  <MessageCircle size={14} />
                  Chat on WhatsApp
                </a>
              ) : (
                <button
                  type="button"
                  data-testid="request-callback-btn"
                  onClick={() => document.getElementById("enquiry-phone")?.focus()}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-[#E2A435] text-[#0D2121] hover:bg-[#c88c2a] transition-colors whitespace-nowrap"
                >
                  <MessageCircle size={14} />
                  Request a Callback
                </button>
              )}
            </div>

            <div className="flex items-center justify-between p-4 bg-[#FAF6F0]/5 border border-[#FAF6F0]/10">
              <div>
                <div className="text-xs text-[#EADBC8]/70">Have drawings ready?</div>
                <div className="text-sm font-medium text-[#FAF6F0]">Book a 30-min Studio Slot</div>
              </div>
              <button
                type="button"
                data-testid="booking-slot-btn"
                onClick={() => setBookingOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-[#FAF6F0]/30 text-[#FAF6F0] hover:bg-[#FAF6F0]/10 transition-colors"
              >
                <CalendarClock size={14} />
                Select Time Slot
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={120}>
          <div className="bg-[#FAF6F0] p-8 sm:p-10 text-[#0D2121] rounded-sm">
            <h3 className="font-display text-2xl font-semibold mb-2">Project Enquiry Form</h3>
            <p className="text-xs text-[#7A6F66] mb-6">Leave your details and we will get back within 24 hours.</p>

            <form className="space-y-4" onSubmit={submit} data-testid="enquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="enquiry-name">Your Name</label>
                  <input
                    id="enquiry-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Aditi Sharma"
                    data-testid="enquiry-name-input"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="enquiry-phone">Phone Number</label>
                  <input
                    id="enquiry-phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="e.g. +91 98765 43210"
                    data-testid="enquiry-phone-input"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="enquiry-type">Project Type</label>
                  <select
                    id="enquiry-type"
                    value={form.project_type}
                    onChange={update("project_type")}
                    data-testid="enquiry-project-type-select"
                    className={inputClass}
                  >
                    <option>New Home / Apartment</option>
                    <option>Home Renovation</option>
                    <option>Commercial Space / Cafe</option>
                    <option>Online Consultation Only</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="enquiry-location">Location / City</label>
                  <input
                    id="enquiry-location"
                    type="text"
                    value={form.location}
                    onChange={update("location")}
                    placeholder="e.g. Nearby City or Remote"
                    data-testid="enquiry-location-input"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="enquiry-message">What do you need done?</label>
                <textarea
                  id="enquiry-message"
                  rows="3"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Briefly describe your space, timeline, and requirements..."
                  data-testid="enquiry-message-input"
                  className={inputClass}
                ></textarea>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="w-full py-4 bg-[#E2A435] hover:bg-[#c88c2a] disabled:opacity-60 text-[#0D2121] font-semibold text-sm uppercase tracking-wider transition-colors"
                >
                  {submitting ? "Sending..." : "Submit Project Enquiry"}
                </button>
                <p className="text-[11px] text-[#7A6F66] text-center mt-3">We respect your privacy and never share your contact details.</p>
              </div>
            </form>
          </div>
        </Reveal>
      </div>

      {bookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D2121]/80 backdrop-blur-sm px-6" data-testid="booking-modal">
          <div className="bg-[#FAF6F0] text-[#0D2121] max-w-md w-full p-8 rounded-sm shadow-xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl font-semibold">Pick a Consultation Slot</h3>
                <p className="text-xs text-[#7A6F66] mt-1">30-minute studio call. We confirm the exact time over phone.</p>
              </div>
              <button
                type="button"
                aria-label="Close booking modal"
                data-testid="booking-modal-close-btn"
                onClick={() => setBookingOpen(false)}
                className="p-1 text-[#7A6F66] hover:text-[#0D2121] transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot.label}
                  type="button"
                  data-testid={slot.testId}
                  onClick={() => pickSlot(slot)}
                  className="w-full flex items-center justify-between p-4 bg-[#F4EDE2] border border-[#0D2121]/15 hover:border-[#E2A435] hover:bg-[#EADBC8] transition-colors text-left"
                >
                  <span className="text-sm font-semibold">{slot.label}</span>
                  <span className="text-xs text-[#7A6F66]">{slot.detail}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
